import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SecondaryBase } from '../Commons/SecondaryBase';
import { useAuth } from '../../Providers/AuthProvider';
import { Confirm } from './../../Commons/Modals/Confirm';
import { useFloatMessage, MESSAGE_TYPE } from './../../Providers/FloatMessage';

/**
 * Resuelve una URL de asset relativa (ej. /uploads/riddles/xxx.webp)
 * anteponiendo el origen del backend si no es una URL absoluta o Data URI.
 */
const resolveAssetUrl = (url) => {
    if (!url || typeof url !== 'string') return '';
    const clean = url.trim();
    if (!clean) return '';
    if (clean.startsWith('http://') || clean.startsWith('https://') || clean.startsWith('data:') || clean.startsWith('blob:')) {
        return clean;
    }
    const apiBase = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';
    const backendOrigin = apiBase.replace(/\/api\/?$/i, '');
    const normalizedPath = clean.startsWith('/') ? clean : `/${clean}`;
    return `${backendOrigin}${normalizedPath}`;
};

/**
 * Componente Dropzone Interactivo para Siluetas y Fotos Reveladas
 * Soporta Drag & Drop, explorador de archivos, preview y subida directa a la API.
 */
function RiddleImageDropzone({
    label,
    sublabel,
    value,
    onChange,
    onUpload,
    isUploading,
    disabled,
    variant = 'hidden' // 'hidden' | 'revealed'
}) {
    const fileInputRef = useRef(null);
    const [isDragOver, setIsDragOver] = useState(false);

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled && !isUploading) {
            setIsDragOver(true);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled && !isUploading) {
            setIsDragOver(true);
        }
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
        if (disabled || isUploading) return;

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            const file = files[0];
            if (file.type.startsWith('image/')) {
                onUpload(file);
            }
        }
    };

    const handleFileSelect = (e) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            const file = files[0];
            if (file.type.startsWith('image/')) {
                onUpload(file);
            }
        }
        // Limpiar el input para permitir volver a subir el mismo archivo si es necesario
        e.target.value = '';
    };

    const isSilhouette = variant === 'hidden';
    const resolvedUrl = resolveAssetUrl(value);

    return (
        <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
                <label className="form-label text-muted small fw-bold mb-0">
                    <i className={`bi ${isSilhouette ? 'bi-moon-stars-fill text-warning' : 'bi-image-fill text-info'} me-1`}></i>
                    {label} <span className="text-danger">*</span>
                </label>
                {value && (
                    <span className="badge bg-success bg-opacity-25 text-success border border-success border-opacity-50 small">
                        <i className="bi bi-check2-circle me-1"></i>Cargada
                    </span>
                )}
            </div>
            {sublabel && <div className="text-muted small mb-2" style={{ fontSize: '0.78rem' }}>{sublabel}</div>}

            <input
                type="file"
                ref={fileInputRef}
                className="d-none"
                accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                onChange={handleFileSelect}
                disabled={disabled || isUploading}
            />

            {value ? (
                // Vista con imagen cargada (Preview)
                <div
                    className="border rounded p-2 position-relative d-flex flex-column align-items-center justify-content-center"
                    style={{
                        minHeight: '160px',
                        backgroundColor: isSilhouette ? '#151b24' : '#1e2430',
                        borderColor: isSilhouette ? '#ffc10744' : '#0dcaf044',
                        backgroundImage: isSilhouette
                            ? 'radial-gradient(circle, #2a3342 10%, transparent 11%)'
                            : 'none',
                        backgroundSize: '12px 12px'
                    }}
                >
                    <div className="position-relative d-flex align-items-center justify-content-center w-100" style={{ height: '140px' }}>
                        <img
                            src={resolvedUrl}
                            alt={label}
                            className="img-fluid rounded"
                            style={{
                                maxHeight: '130px',
                                maxWidth: '100%',
                                objectFit: 'contain',
                                filter: isSilhouette ? 'drop-shadow(0 2px 8px rgba(0,0,0,0.6))' : 'none'
                            }}
                            onError={(e) => {
                                e.target.style.display = 'none';
                            }}
                        />
                    </div>

                    <div className="d-flex gap-2 mt-2 w-100 justify-content-center">
                        <button
                            type="button"
                            className="btn btn-outline-light btn-sm py-1 px-2"
                            style={{ fontSize: '0.8rem' }}
                            onClick={() => fileInputRef.current && fileInputRef.current.click()}
                            disabled={disabled || isUploading}
                            title="Subir otra imagen para reemplazar"
                        >
                            <i className="bi bi-arrow-repeat me-1"></i>Cambiar
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-danger btn-sm py-1 px-2"
                            style={{ fontSize: '0.8rem' }}
                            onClick={() => onChange('')}
                            disabled={disabled || isUploading}
                            title="Quitar esta imagen"
                        >
                            <i className="bi bi-trash me-1"></i>Quitar
                        </button>
                    </div>

                    {isUploading && (
                        <div className="position-absolute top-0 start-0 w-100 h-100 bg-dark bg-opacity-75 d-flex flex-column align-items-center justify-content-center rounded">
                            <div className="spinner-border spinner-border-sm text-info mb-2" role="status"></div>
                            <span className="small text-white">Subiendo y procesando...</span>
                        </div>
                    )}
                </div>
            ) : (
                // Vista Dropzone vacía
                <div
                    className={`border border-2 border-dashed rounded p-3 text-center position-relative transition-all ${
                        isDragOver
                            ? 'border-info bg-info bg-opacity-10 shadow-sm'
                            : 'border-secondary bg-dark bg-opacity-50'
                    }`}
                    style={{
                        cursor: disabled || isUploading ? 'not-allowed' : 'pointer',
                        minHeight: '140px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease-in-out'
                    }}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => {
                        if (!disabled && !isUploading && fileInputRef.current) {
                            fileInputRef.current.click();
                        }
                    }}
                >
                    {isUploading ? (
                        <div className="py-3">
                            <div className="spinner-border spinner-border-sm text-info mb-2" role="status"></div>
                            <div className="small text-white">Subiendo y procesando archivo...</div>
                        </div>
                    ) : (
                        <>
                            <i className={`bi ${isSilhouette ? 'bi-cloud-moon' : 'bi-cloud-arrow-up'} fs-2 mb-1 ${isDragOver ? 'text-info' : 'text-secondary'}`}></i>
                            <div className="small text-white fw-bold">
                                {isDragOver ? '¡Suelta la imagen aquí!' : 'Arrastra o haz clic para subir'}
                            </div>
                            <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                                PNG, JPG, WebP o SVG (máx. 25MB)
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

export default function RiddleManager({ getOutOfTheDynamic }) {
    const { authenticatedFetch } = useAuth();
    const { showMessage } = useFloatMessage();
    const [searchParams, setSearchParams] = useSearchParams();

    // Eventos
    const [events, setEvents] = useState([]);
    const [selectedEventId, setSelectedEventId] = useState(searchParams.get('eventId') || '');

    // Adivinanzas
    const [riddles, setRiddles] = useState([]);
    const [selectedRiddle, setSelectedRiddle] = useState(null);

    // Formulario
    const [riddleName, setRiddleName] = useState('');
    const [questions, setQuestions] = useState([]); // [{ tempId, id, instruction, hiddenImageUrl, revealedImageUrl, revealedText, options, correctAnswer, sortOrder }]

    // Estados UI
    const [loadingEvents, setLoadingEvents] = useState(false);
    const [loadingRiddles, setLoadingRiddles] = useState(false);
    const [actionInProgress, setActionInProgress] = useState(false);
    const [uploadingSlots, setUploadingSlots] = useState({}); // { `${tempId}_hidden`: bool, `${tempId}_revealed`: bool }

    // Modal de confirmación
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmModalData, setConfirmModalData] = useState(null);

    const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';

    // 1. Cargar lista de eventos activos
    const fetchEvents = useCallback(async () => {
        setLoadingEvents(true);
        try {
            const res = await authenticatedFetch(`${API_URL}v2/events`);
            if (res.ok) {
                const data = await res.json();
                const activeEvents = data.filter((e) => !e.isDeleted);
                setEvents(activeEvents);

                // Si no hay evento seleccionado o el seleccionado ya no existe, preseleccionar el primero
                const paramEventId = searchParams.get('eventId');
                if (paramEventId && activeEvents.some((e) => e.id === paramEventId)) {
                    setSelectedEventId(paramEventId);
                } else if (activeEvents.length > 0 && !selectedEventId) {
                    setSelectedEventId(activeEvents[0].id);
                }
            } else {
                console.error("Error al cargar eventos:", res.statusText);
            }
        } catch (error) {
            console.error("Error fetching events:", error);
        } finally {
            setLoadingEvents(false);
        }
    }, [API_URL, authenticatedFetch, searchParams, selectedEventId]);

    useEffect(() => {
        fetchEvents();
    }, [fetchEvents]);

    // 2. Cargar adivinanzas cuando cambia el evento seleccionado
    const fetchRiddles = useCallback(async (eventId) => {
        if (!eventId) {
            setRiddles([]);
            return;
        }
        setLoadingRiddles(true);
        try {
            const res = await authenticatedFetch(`${API_URL}v2/events/${eventId}/riddles`);
            if (res.ok) {
                const data = await res.json();
                setRiddles(data);
            } else {
                console.error("Error al obtener adivinanzas del evento:", res.statusText);
                setRiddles([]);
            }
        } catch (error) {
            console.error("Error fetching riddles:", error);
            setRiddles([]);
        } finally {
            setLoadingRiddles(false);
        }
    }, [API_URL, authenticatedFetch]);

    useEffect(() => {
        if (selectedEventId) {
            fetchRiddles(selectedEventId);
        } else {
            setRiddles([]);
        }
    }, [selectedEventId, fetchRiddles]);

    // Cambiar evento en el selector
    const handleEventChange = (e) => {
        const newEventId = e.target.value;
        setSelectedEventId(newEventId);
        setSearchParams(newEventId ? { eventId: newEventId } : {});
        handleCancelEdit();
    };

    // 3. Seleccionar adivinanza para editar
    const handleSelectRiddle = async (riddleItem) => {
        setActionInProgress(true);
        try {
            const res = await authenticatedFetch(`${API_URL}v2/riddles/${riddleItem.id}`);
            if (res.ok) {
                const data = await res.json();
                setSelectedRiddle(data);
                setRiddleName(data.name || '');
                setQuestions((data.questions || []).map((q, idx) => ({
                    tempId: q.id || Math.random().toString(36).substring(2, 9),
                    id: q.id,
                    instruction: q.instruction || '',
                    hiddenImageUrl: q.hiddenImageUrl || '',
                    revealedImageUrl: q.revealedImageUrl || '',
                    revealedText: q.revealedText || '',
                    options: (q.options && q.options.length > 0) ? q.options : ['', '', '', ''],
                    correctAnswer: q.correctAnswer || '',
                    sortOrder: q.sortOrder !== undefined ? q.sortOrder : idx
                })));
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: 'No se pudo cargar el detalle de la adivinanza.'
                });
            }
        } catch (error) {
            console.error("Error fetching riddle details:", error);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de conexión al cargar la adivinanza.'
            });
        } finally {
            setActionInProgress(false);
        }
    };

    // 4. Crear nueva adivinanza
    const handleAddNewRiddle = () => {
        if (!selectedEventId) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Por favor selecciona un evento antes de crear una adivinanza.'
            });
            return;
        }

        setSelectedRiddle({ id: 'new', eventId: selectedEventId });
        setRiddleName('');
        setQuestions([
            {
                tempId: Math.random().toString(36).substring(2, 9),
                instruction: '¿Quién se esconde en la sombra?',
                hiddenImageUrl: '',
                revealedImageUrl: '',
                revealedText: '',
                options: ['', '', '', ''],
                correctAnswer: '',
                sortOrder: 0
            }
        ]);
    };

    const handleCancelEdit = () => {
        setSelectedRiddle(null);
        setRiddleName('');
        setQuestions([]);
        setUploadingSlots({});
    };

    // 5. Manejo dinámico de Preguntas
    const handleAddQuestion = () => {
        setQuestions([
            ...questions,
            {
                tempId: Math.random().toString(36).substring(2, 9),
                instruction: '¿Quién se esconde en la sombra?',
                hiddenImageUrl: '',
                revealedImageUrl: '',
                revealedText: '',
                options: ['', '', '', ''],
                correctAnswer: '',
                sortOrder: questions.length
            }
        ]);
    };

    const handleRemoveQuestion = (tempId) => {
        if (questions.length <= 1) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'La adivinanza debe contener al menos una pregunta.'
            });
            return;
        }
        setQuestions(questions.filter((q) => q.tempId !== tempId));
    };

    const handleQuestionFieldChange = (tempId, field, value) => {
        setQuestions(questions.map((q) => (q.tempId === tempId ? { ...q, [field]: value } : q)));
    };

    // 6. Manejo de Opciones y Respuesta Correcta
    const handleOptionChange = (qTempId, optIdx, newVal) => {
        setQuestions(questions.map((q) => {
            if (q.tempId === qTempId) {
                const oldVal = q.options[optIdx];
                const newOpts = [...q.options];
                newOpts[optIdx] = newVal;

                // Si la opción que se está editando era la respuesta correcta, sincronizar el correctAnswer
                let updatedCorrect = q.correctAnswer;
                if (q.correctAnswer === oldVal) {
                    updatedCorrect = newVal;
                }

                return { ...q, options: newOpts, correctAnswer: updatedCorrect };
            }
            return q;
        }));
    };

    const handleAddOptionToQuestion = (qTempId) => {
        setQuestions(questions.map((q) => {
            if (q.tempId === qTempId) {
                if (q.options.length >= 5) {
                    showMessage({
                        type: MESSAGE_TYPE.BAD,
                        text: 'El número máximo de opciones por pregunta es 5.'
                    });
                    return q;
                }
                return { ...q, options: [...q.options, ''] };
            }
            return q;
        }));
    };

    const handleRemoveOptionFromQuestion = (qTempId, optIdx) => {
        setQuestions(questions.map((q) => {
            if (q.tempId === qTempId) {
                if (q.options.length <= 2) {
                    showMessage({
                        type: MESSAGE_TYPE.BAD,
                        text: 'Cada pregunta debe tener al menos 2 opciones.'
                    });
                    return q;
                }
                const removedVal = q.options[optIdx];
                const newOpts = q.options.filter((_, idx) => idx !== optIdx);
                const updatedCorrect = q.correctAnswer === removedVal ? '' : q.correctAnswer;
                return { ...q, options: newOpts, correctAnswer: updatedCorrect };
            }
            return q;
        }));
    };

    const handleSetCorrectAnswer = (qTempId, answerVal) => {
        setQuestions(questions.map((q) => (q.tempId === qTempId ? { ...q, correctAnswer: answerVal } : q)));
    };

    // 7. Subida de archivos a la API
    const handleUploadAsset = async (qTempId, fieldType, file) => {
        const slotKey = `${qTempId}_${fieldType}`;
        setUploadingSlots((prev) => ({ ...prev, [slotKey]: true }));

        try {
            const formData = new FormData();
            formData.append('file', file);

            const res = await authenticatedFetch(`${API_URL}v2/riddles/upload`, {
                method: 'POST',
                body: formData
            });

            if (res.ok) {
                const data = await res.json();
                const uploadedUrl = data.url;
                handleQuestionFieldChange(qTempId, fieldType === 'hidden' ? 'hiddenImageUrl' : 'revealedImageUrl', uploadedUrl);
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: `Imagen ${fieldType === 'hidden' ? 'de silueta' : 'revelada'} subida correctamente.`
                });
            } else {
                const errText = await res.text();
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: `Error al subir imagen: ${errText || 'Formato o tamaño no válido.'}`
                });
            }
        } catch (error) {
            console.error("Error uploading asset:", error);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de conexión al subir la imagen.'
            });
        } finally {
            setUploadingSlots((prev) => ({ ...prev, [slotKey]: false }));
        }
    };

    // 8. Validación y Guardado de la Adivinanza
    const handleSave = async (e) => {
        e.preventDefault();

        if (!selectedEventId) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Debes seleccionar un evento válido.' });
            return;
        }

        if (!riddleName.trim()) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'El nombre del cuestionario de adivinanzas es obligatorio.' });
            return;
        }

        if (questions.length === 0) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Debe haber al menos 1 pregunta en la adivinanza.' });
            return;
        }

        // Validaciones estrictas por pregunta
        for (let i = 0; i < questions.length; i++) {
            const q = questions[i];
            const num = i + 1;

            if (!q.instruction || !q.instruction.trim()) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: `La pregunta #${num} no tiene texto de instrucción.` });
                return;
            }

            if (!q.hiddenImageUrl || !q.hiddenImageUrl.trim()) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: `La pregunta #${num} requiere la imagen de silueta (HiddenImageUrl).` });
                return;
            }

            if (!q.revealedImageUrl || !q.revealedImageUrl.trim()) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: `La pregunta #${num} requiere la foto revelada (RevealedImageUrl).` });
                return;
            }

            if (!q.revealedText || !q.revealedText.trim()) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: `La pregunta #${num} requiere el texto de la solución/nombre revelado.` });
                return;
            }

            const cleanOptions = (q.options || []).map((o) => o.trim());
            if (cleanOptions.length < 2 || cleanOptions.length > 5) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: `La pregunta #${num} debe tener entre 2 y 5 opciones de respuesta.` });
                return;
            }

            if (cleanOptions.some((o) => !o)) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: `Todas las opciones de la pregunta #${num} deben contener texto.` });
                return;
            }

            if (!q.correctAnswer || !q.correctAnswer.trim()) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: `Selecciona la respuesta correcta para la pregunta #${num}.` });
                return;
            }

            const isMatch = cleanOptions.some(
                (o) => o.toLowerCase() === q.correctAnswer.trim().toLowerCase()
            );
            if (!isMatch) {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: `La respuesta correcta de la pregunta #${num} debe coincidir con una de sus opciones.`
                });
                return;
            }
        }

        setActionInProgress(true);

        const isNew = selectedRiddle.id === 'new';
        const payload = {
            eventId: selectedEventId,
            name: riddleName.trim(),
            questions: questions.map((q, idx) => ({
                instruction: q.instruction.trim(),
                hiddenImageUrl: q.hiddenImageUrl.trim(),
                revealedImageUrl: q.revealedImageUrl.trim(),
                revealedText: q.revealedText.trim(),
                options: q.options.map((o) => o.trim()),
                correctAnswer: q.correctAnswer.trim(),
                sortOrder: idx
            }))
        };

        const url = isNew ? `${API_URL}v2/riddles` : `${API_URL}v2/riddles/${selectedRiddle.id}`;
        const method = isNew ? 'POST' : 'PUT';

        try {
            const res = await authenticatedFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: isNew ? '¡Cuestionario de adivinanzas creado con éxito!' : '¡Adivinanza actualizada correctamente!'
                });
                handleCancelEdit();
                fetchRiddles(selectedEventId);
            } else {
                const errData = await res.json().catch(() => ({}));
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: errData.message || (typeof errData === 'string' ? errData : 'Error al guardar la adivinanza.')
                });
            }
        } catch (error) {
            console.error("Error saving riddle:", error);
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error de conexión al guardar la adivinanza.' });
        } finally {
            setActionInProgress(false);
        }
    };

    // 9. Confirmación y Eliminación de Adivinanza
    const confirmDeleteRiddle = (riddleItem) => {
        const executeDelete = async () => {
            try {
                const res = await authenticatedFetch(`${API_URL}v2/riddles/${riddleItem.id}`, {
                    method: 'DELETE'
                });

                if (res.ok) {
                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Adivinanza eliminada exitosamente.' });
                    fetchRiddles(selectedEventId);
                    if (selectedRiddle && selectedRiddle.id === riddleItem.id) {
                        handleCancelEdit();
                    }
                    return { success: true };
                } else {
                    const errMsg = await res.text();
                    return { success: false, message: `No se pudo eliminar: ${errMsg}` };
                }
            } catch (error) {
                console.error("Error deleting riddle:", error);
                return { success: false, message: 'Error de conexión al eliminar la adivinanza.' };
            }
        };

        setConfirmModalData({
            message: `¿Estás seguro de que deseas eliminar permanentemente la adivinanza "${riddleItem.name}"?`,
            actionToYesButton: executeDelete,
            textYesButton: 'Sí, eliminar',
            textNoButton: 'Cancelar'
        });
        setShowConfirmModal(true);
    };

    const currentEvent = events.find((e) => e.id === selectedEventId);

    return (
        <SecondaryBase
            actionToExecute={getOutOfTheDynamic}
        >
            <div className="container-fluid py-3 text-white">
                {/* Header Superior y Selector de Evento */}
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 pb-3 border-bottom border-secondary">
                    <div>
                        <h2 className="fw-bold mb-1">
                            <i className="bi bi-patch-question-fill text-warning me-2"></i>
                            Gestor de Adivinanzas (Siluetas)
                        </h2>
                        <p className="text-muted mb-0 small">
                            Configura dinámicas interactivas con siluetas sombreadas, fotos reales y opciones múltiples por evento.
                        </p>
                    </div>

                    {/* Selector de Evento */}
                    <div className="d-flex align-items-center gap-2 mt-2 mt-md-0">
                        <label className="text-muted small fw-bold mb-0 text-nowrap">
                            <i className="bi bi-calendar-event me-1 text-info"></i> Evento Activo:
                        </label>
                        <select
                            className="form-select form-select-sm bg-dark text-white border-secondary"
                            style={{ minWidth: '220px' }}
                            value={selectedEventId}
                            onChange={handleEventChange}
                            disabled={loadingEvents || actionInProgress}
                        >
                            {events.length === 0 ? (
                                <option value="">No hay eventos disponibles</option>
                            ) : (
                                events.map((ev) => (
                                    <option key={ev.id} value={ev.id}>
                                        {ev.nombre} ({new Date(ev.fecha).toLocaleDateString()})
                                    </option>
                                ))
                            )}
                        </select>
                    </div>
                </div>

                <div className="row">
                    {/* Columna Izquierda: Listado de Adivinanzas por Evento */}
                    <div className="col-lg-4 col-md-5 mb-4">
                        <div className="card bg-dark border-secondary h-100 shadow-sm">
                            <div className="card-header border-secondary d-flex justify-content-between align-items-center bg-secondary bg-opacity-10 py-3">
                                <div>
                                    <h5 className="mb-0 text-warning fw-bold small text-uppercase tracking-wider">
                                        <i className="bi bi-collection-play me-1"></i> Adivinanzas del Evento
                                    </h5>
                                    {currentEvent && (
                                        <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                                            {currentEvent.nombre}
                                        </span>
                                    )}
                                </div>
                                <button
                                    className="btn btn-outline-warning btn-sm fw-bold"
                                    onClick={handleAddNewRiddle}
                                    disabled={!selectedEventId || loadingRiddles || actionInProgress}
                                >
                                    <i className="bi bi-plus-lg me-1"></i>Nueva
                                </button>
                            </div>

                            <div className="card-body p-0" style={{ maxHeight: '620px', overflowY: 'auto' }}>
                                {loadingRiddles ? (
                                    <div className="text-center py-5 text-muted">
                                        <div className="spinner-border spinner-border-sm text-warning mb-2" role="status"></div>
                                        <div>Cargando adivinanzas...</div>
                                    </div>
                                ) : !selectedEventId ? (
                                    <div className="text-center py-5 text-muted small">
                                        <i className="bi bi-info-circle fs-3 mb-2 d-block"></i>
                                        Selecciona un evento para ver sus adivinanzas.
                                    </div>
                                ) : riddles.length === 0 ? (
                                    <div className="text-center py-5 text-muted small px-3">
                                        <i className="bi bi-question-diamond fs-2 mb-2 d-block text-secondary"></i>
                                        No hay adivinanzas configuradas para este evento.<br />
                                        Haz clic en <strong>+ Nueva</strong> para crear la primera.
                                    </div>
                                ) : (
                                    <div className="list-group list-group-flush bg-transparent">
                                        {riddles.map((r) => {
                                            const isSelected = selectedRiddle?.id === r.id;
                                            return (
                                                <div
                                                    key={r.id}
                                                    className={`list-group-item list-group-item-action bg-transparent text-white border-secondary d-flex justify-content-between align-items-center py-3 ${
                                                        isSelected ? 'active bg-secondary bg-opacity-50 text-warning border-warning' : ''
                                                    }`}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleSelectRiddle(r)}
                                                >
                                                    <div className="me-2 text-truncate">
                                                        <div className="fw-bold text-truncate" title={r.name}>
                                                            {r.name}
                                                        </div>
                                                        <div className="small text-muted" style={{ fontSize: '0.78rem' }}>
                                                            <i className="bi bi-layers me-1"></i>
                                                            {r.questionsCount} {r.questionsCount === 1 ? 'pregunta' : 'preguntas'}
                                                            <span className="mx-1">•</span>
                                                            <i className="bi bi-clock me-1"></i>
                                                            {new Date(r.createdAt).toLocaleDateString()}
                                                        </div>
                                                    </div>
                                                    <div className="d-flex gap-1">
                                                        <button
                                                            className="btn btn-outline-danger btn-sm border-0 py-1 px-2"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                confirmDeleteRiddle(r);
                                                            }}
                                                            disabled={actionInProgress}
                                                            title="Eliminar adivinanza"
                                                        >
                                                            <i className="bi bi-trash-fill"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Columna Derecha: Formulario de Creación / Edición */}
                    <div className="col-lg-8 col-md-7 mb-4">
                        {selectedRiddle ? (
                            <div className="card bg-dark border-secondary shadow-sm">
                                <div className="card-header border-secondary bg-secondary bg-opacity-10 d-flex justify-content-between align-items-center py-3">
                                    <h5 className="mb-0 text-warning fw-bold">
                                        <i className={`bi ${selectedRiddle.id === 'new' ? 'bi-plus-circle' : 'bi-pencil-square'} me-2`}></i>
                                        {selectedRiddle.id === 'new' ? 'Crear Nueva Adivinanza' : 'Editar Adivinanza'}
                                    </h5>
                                    <span className="badge bg-dark border border-secondary text-info small">
                                        {questions.length} {questions.length === 1 ? 'Pregunta' : 'Preguntas'}
                                    </span>
                                </div>

                                <form onSubmit={handleSave}>
                                    <div className="card-body">
                                        {/* Nombre de la Adivinanza */}
                                        <div className="mb-4">
                                            <label className="form-label text-muted small fw-bold">
                                                Nombre de la Adivinanza <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control bg-transparent text-white border-secondary"
                                                placeholder="Ej: Adivina el Jugador Misterioso - Jornada 5"
                                                value={riddleName}
                                                onChange={(e) => setRiddleName(e.target.value)}
                                                required
                                                disabled={actionInProgress}
                                                maxLength={200}
                                            />
                                        </div>

                                        <hr className="border-secondary mb-4" />

                                        {/* Listado Dinámico de Preguntas */}
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <div>
                                                <h6 className="mb-0 text-warning fw-bold">
                                                    <i className="bi bi-question-square-fill me-2"></i>
                                                    Preguntas de la Adivinanza
                                                </h6>
                                                <span className="text-muted small" style={{ fontSize: '0.78rem' }}>
                                                    Cada pregunta incluye silueta oculta, foto revelada, texto de solución y entre 2 y 5 opciones.
                                                </span>
                                            </div>
                                            <button
                                                type="button"
                                                className="btn btn-outline-warning btn-sm fw-bold"
                                                onClick={handleAddQuestion}
                                                disabled={actionInProgress}
                                            >
                                                <i className="bi bi-plus-lg me-1"></i>Agregar Pregunta
                                            </button>
                                        </div>

                                        <div style={{ maxHeight: '520px', overflowY: 'auto', paddingRight: '4px' }}>
                                            {questions.map((q, qIdx) => (
                                                <div key={q.tempId} className="card bg-dark border-secondary mb-4 shadow-sm">
                                                    {/* Header de la Pregunta */}
                                                    <div className="card-header border-secondary bg-secondary bg-opacity-25 d-flex justify-content-between align-items-center py-2">
                                                        <span className="fw-bold text-warning small">
                                                            <i className="bi bi-hash me-1"></i>Pregunta #{qIdx + 1}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-danger btn-sm py-0 border-0"
                                                            onClick={() => handleRemoveQuestion(q.tempId)}
                                                            disabled={actionInProgress || questions.length <= 1}
                                                            title="Eliminar esta pregunta"
                                                        >
                                                            <i className="bi bi-x-circle-fill fs-5"></i>
                                                        </button>
                                                    </div>

                                                    <div className="card-body">
                                                        {/* Instrucción */}
                                                        <div className="mb-3">
                                                            <label className="form-label text-muted small fw-bold">
                                                                Instrucción / Pregunta <span className="text-danger">*</span>
                                                            </label>
                                                            <input
                                                                type="text"
                                                                className="form-control bg-transparent text-white border-secondary"
                                                                placeholder="Ej: ¿Quién se esconde en la sombra?"
                                                                value={q.instruction}
                                                                onChange={(e) => handleQuestionFieldChange(q.tempId, 'instruction', e.target.value)}
                                                                required
                                                                disabled={actionInProgress}
                                                            />
                                                        </div>

                                                        {/* Doble Dropzone: Silueta vs Foto Revelada */}
                                                        <div className="row g-3 mb-3">
                                                            <div className="col-md-6">
                                                                <RiddleImageDropzone
                                                                    label="Silueta (Imagen Oculta)"
                                                                    sublabel="Imagen que ven los aficionados al inicio"
                                                                    value={q.hiddenImageUrl}
                                                                    onChange={(newVal) => handleQuestionFieldChange(q.tempId, 'hiddenImageUrl', newVal)}
                                                                    onUpload={(file) => handleUploadAsset(q.tempId, 'hidden', file)}
                                                                    isUploading={!!uploadingSlots[`${q.tempId}_hidden`]}
                                                                    disabled={actionInProgress}
                                                                    variant="hidden"
                                                                />
                                                            </div>
                                                            <div className="col-md-6">
                                                                <RiddleImageDropzone
                                                                    label="Foto Revelada (Solución)"
                                                                    sublabel="Imagen real a color que se revela al final"
                                                                    value={q.revealedImageUrl}
                                                                    onChange={(newVal) => handleQuestionFieldChange(q.tempId, 'revealedImageUrl', newVal)}
                                                                    onUpload={(file) => handleUploadAsset(q.tempId, 'revealed', file)}
                                                                    isUploading={!!uploadingSlots[`${q.tempId}_revealed`]}
                                                                    disabled={actionInProgress}
                                                                    variant="revealed"
                                                                />
                                                            </div>
                                                        </div>

                                                        {/* Texto Revelado / Solución */}
                                                        <div className="mb-3">
                                                            <label className="form-label text-muted small fw-bold">
                                                                Texto de Solución Revelado <span className="text-danger">*</span>
                                                            </label>
                                                            <input
                                                                type="text"
                                                                className="form-control bg-transparent text-white border-secondary"
                                                                placeholder="Ej: André-Pierre Gignac"
                                                                value={q.revealedText}
                                                                onChange={(e) => handleQuestionFieldChange(q.tempId, 'revealedText', e.target.value)}
                                                                required
                                                                disabled={actionInProgress}
                                                            />
                                                            <span className="text-muted small" style={{ fontSize: '0.72rem' }}>
                                                                Nombre o descripción que aparecerá en pantalla cuando se revele la respuesta.
                                                            </span>
                                                        </div>

                                                        {/* Editor de Opciones de Respuesta */}
                                                        <div className="mb-2">
                                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                                <label className="form-label text-muted small fw-bold mb-0">
                                                                    Opciones de Respuesta ({q.options.length}/5) <span className="text-danger">*</span>
                                                                </label>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-link text-warning p-0 text-decoration-none small"
                                                                    style={{ fontSize: '0.8rem' }}
                                                                    onClick={() => handleAddOptionToQuestion(q.tempId)}
                                                                    disabled={actionInProgress || q.options.length >= 5}
                                                                >
                                                                    + Agregar Opción
                                                                </button>
                                                            </div>

                                                            <div className="text-muted small mb-2" style={{ fontSize: '0.72rem' }}>
                                                                Marca el botón circular <strong>( )</strong> a la izquierda de la opción que sea la respuesta correcta.
                                                            </div>

                                                            {q.options.map((opt, optIdx) => {
                                                                const letter = String.fromCharCode(65 + optIdx);
                                                                const isCorrect = q.correctAnswer && q.correctAnswer.trim() === opt.trim() && opt.trim() !== '';
                                                                return (
                                                                    <div key={optIdx} className="input-group mb-2">
                                                                        {/* Radio button selector de respuesta correcta */}
                                                                        <div className="input-group-text bg-dark border-secondary">
                                                                            <input
                                                                                className="form-check-input mt-0"
                                                                                type="radio"
                                                                                name={`correct_${q.tempId}`}
                                                                                checked={isCorrect}
                                                                                onChange={() => handleSetCorrectAnswer(q.tempId, opt)}
                                                                                disabled={actionInProgress || !opt.trim()}
                                                                                title="Marcar como respuesta correcta"
                                                                                style={{ cursor: opt.trim() ? 'pointer' : 'not-allowed' }}
                                                                            />
                                                                        </div>

                                                                        {/* Letra A, B, C... */}
                                                                        <span
                                                                            className={`input-group-text fw-bold border-secondary ${
                                                                                isCorrect ? 'bg-warning text-dark' : 'bg-secondary text-white'
                                                                            }`}
                                                                            style={{ minWidth: '40px', justifyContent: 'center' }}
                                                                        >
                                                                            {letter}
                                                                        </span>

                                                                        {/* Texto de la opción */}
                                                                        <input
                                                                            type="text"
                                                                            className={`form-control bg-transparent text-white border-secondary ${
                                                                                isCorrect ? 'border-warning' : ''
                                                                            }`}
                                                                            placeholder={`Opción ${letter}`}
                                                                            value={opt}
                                                                            onChange={(e) => handleOptionChange(q.tempId, optIdx, e.target.value)}
                                                                            required
                                                                            disabled={actionInProgress}
                                                                        />

                                                                        {/* Botón para quitar opción */}
                                                                        {q.options.length > 2 && (
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-outline-danger"
                                                                                onClick={() => handleRemoveOptionFromQuestion(q.tempId, optIdx)}
                                                                                disabled={actionInProgress}
                                                                                title="Eliminar opción"
                                                                            >
                                                                                <i className="bi bi-dash-lg"></i>
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}

                                                            {/* Alerta si no se ha seleccionado respuesta correcta */}
                                                            {(!q.correctAnswer || !q.options.includes(q.correctAnswer)) && (
                                                                <div className="text-danger small mt-1" style={{ fontSize: '0.75rem' }}>
                                                                    <i className="bi bi-exclamation-triangle-fill me-1"></i>
                                                                    Debes seleccionar una opción como respuesta correcta.
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Footer del Formulario */}
                                    <div className="card-footer border-secondary d-flex justify-content-end gap-2 bg-transparent py-3">
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={handleCancelEdit}
                                            disabled={actionInProgress}
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-warning px-4 fw-bold"
                                            disabled={actionInProgress}
                                        >
                                            {actionInProgress ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                    Guardando...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bi bi-check-lg me-1"></i>
                                                    Guardar Adivinanza
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        ) : (
                            <div
                                className="h-100 d-flex flex-column align-items-center justify-content-center border border-secondary border-dashed rounded p-5 text-muted bg-dark bg-opacity-25"
                                style={{ minHeight: '400px' }}
                            >
                                <i className="bi bi-patch-question fs-1 text-secondary mb-3"></i>
                                <h5 className="text-white-50">Ninguna adivinanza seleccionada</h5>
                                <p className="small text-muted text-center" style={{ maxWidth: '400px' }}>
                                    Selecciona una adivinanza de la lista de la izquierda para editar sus preguntas, o crea una nueva con el botón <strong>+ Nueva</strong>.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Modal de Confirmación para Eliminar */}
            {showConfirmModal && (
                <Confirm
                    message={confirmModalData?.message}
                    actionToYesButton={confirmModalData?.actionToYesButton}
                    textYesButton={confirmModalData?.textYesButton}
                    textNoButton={confirmModalData?.textNoButton}
                    setShowModal={setShowConfirmModal}
                    setConfirmData={setConfirmModalData}
                />
            )}
        </SecondaryBase>
    );
}
